import { MessageCircleIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { Reveal } from '../shared/Reveal';
import { ImageSlot } from '../shared/ImageSlot';
import { placeholderImages } from '../../data/placeholderImages';
import { mediaOr } from '../../core/rules/media';
import { ContactVisit, VISIT_ID } from './ContactVisit';
import { LocalIntro } from './LocalIntro';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { telHref } from '../../core/rules/contact';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { heroTitle } from '../../ui/sections';
import type { ContactChannel } from '../../core/types/pages';
type Action = {
  kind: 'link';
  href: string;
  external: boolean;
  srSuffix?: string;
} | {
  kind: 'button';
  onClick: () => void;
};
const ACTION_CLS = 'group inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap text-[15px] text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline';

/**
 * Contact, gebouwd rond vijf bezoekers:
 * 1. "Ik heb een vraag": WhatsApp bovenaan, zoals Sarah echt werkt.
 * 2. "Waar zit ze?": in de hero een sprong naar Langskomen, waar het adres één keer staat.
 * 3. "Ik kom voor het eerst": onder "Langskomen" kaart, route, OV, parkeren, aanbellen en tijden.
 * 4. "Ik ben te laat": een eigen regel met de snelste weg (WhatsApp) en de verzetregel.
 * 5. "Mijn bestelling": mail met het onderwerp al ingevuld.
 * Adres, telefoon en openingstijden komen uit de instellingen; alleen de woorden uit Paginateksten.
 */
export function Contact() {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.contact;
  const orderMail = `mailto:${salon.email}?subject=${encodeURIComponent('Vraag over mijn bestelling')}`;
  const whatsapp: Action = {
    kind: 'link',
    href: salon.whatsapp,
    external: true,
    srSuffix: shopLabels.viaWhatsApp
  };
  const channels: {
    key: string;
    channel: ContactChannel | undefined;
    action: Action;
  }[] = [{
    key: 'skin',
    channel: copy.channels.skin,
    action: whatsapp
  },
  // Behandeling of online consult: één kanaal, één knop; in de lade kies je wat.
  {
    key: 'booking',
    channel: copy.channels.booking,
    action: {
      kind: 'button',
      onClick: () => openBooking()
    }
  }, {
    key: 'order',
    channel: copy.channels.order,
    action: {
      kind: 'link',
      href: orderMail,
      external: false
    }
  },
  // Oudere paginateksten uit de backend kennen dit kanaal nog niet: dan blijft het weg.
  {
    key: 'late',
    channel: copy.channels.late,
    action: whatsapp
  }];
  const visible = channels.filter((c): c is typeof c & {
    channel: ContactChannel;
  } => Boolean(c.channel?.title));
  return <>
      <section className="bg-sand" data-id="element-7427" data-mp="1tsaewti31yap">
        <div className="mx-auto grid max-w-content gap-14 px-6 pb-20 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-7428" data-mp="1tr8rdpjvrjc3">
          <div className="lg:col-span-5" data-id="element-7429" data-mp="1hahkr36pytuv">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7430" data-mp="1h96f0xtprc14">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 lg:mt-5 ${heroTitle}`} data-id="element-7431" data-mp="1ca397b5wt1s3">
              <HeadingText heading={copy.hero.heading} accentBlock data-id="element-7432" data-mp="18b2ti1iqaf9b" />
            </h1>
            {copy.hero.body && <p className="mt-7 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7433" data-mp="13bhszmnwtyuh">{fill(copy.hero.body)}</p>}

            {(copy.hero.cta || copy.hero.secondaryCta) && <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-7434" data-mp="tletr4q73onp">
                {copy.hero.cta && <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnPrimaryLg} data-id="element-7435" data-mp="omtzdlxmjz76">
                    <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7436" data-mp="kjp0dtcr6fna" />
                    {fill(copy.hero.cta)}
                    <span className="sr-only" data-id="element-7437" data-mp="fnqhume3vrus">{shopLabels.viaWhatsApp}</span>
                  </a>}
                {copy.hero.secondaryCta && salon.email && <a href={`mailto:${salon.email}`} className={btnSecondaryLg} data-id="element-7438" data-mp="2joyutp37ph2j">
                    <MailIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7439" data-mp="25x0t3zbtynge" />
                    {fill(copy.hero.secondaryCta)}
                  </a>}
              </div>}

            {/* Wie het liever direct ziet: nummer, mail en adres als tekst, kopieerbaar. */}
            <dl className="mt-10 space-y-3 border-t border-ink/15 pt-6 text-[15px]" data-id="element-7440" data-mp="21022vrvaf0nh">
              {salon.phone && <div className="flex gap-3" data-id="element-7441" data-mp="1n0sn2tazu8k1">
                  <dt className="sr-only" data-id="element-7442" data-mp="1ry11cit0tsg0">Telefoon en WhatsApp</dt>
                  <PhoneIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7443" data-mp="1s0eycx79heq7" />
                  <dd data-id="element-7444" data-mp="3hbee8wf78osq">
                    <a href={telHref(salon.phoneIntl)} className="text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7445" data-mp="3mcvcsla7nz65">
                      <span className="sr-only" data-id="element-7446" data-mp="bnv0620bu2u">Bel </span>
                      {salon.phone}
                    </a>
                    <span className="text-muted" data-id="element-7447" data-mp="3rj0fov23mpni"> · ook WhatsApp</span>
                  </dd>
                </div>}
              {salon.email && <div className="flex gap-3" data-id="element-7448" data-mp="4bewp4t2num5">
                  <dt className="sr-only" data-id="element-7449" data-mp="4adb46efjpq6">E-mail</dt>
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7450" data-mp="j0b2ev7novjl" />
                  <dd className="min-w-0" data-id="element-7451" data-mp="e1ho50kjp0id">
                    <a href={`mailto:${salon.email}`} className="break-all text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7452" data-mp="e4nf6lcqicgq">
                      {salon.email}
                    </a>
                  </dd>
                </div>}
              {/* Het volledige adres staat één keer, bij Langskomen; hier alleen de sprong erheen. */}
              {salon.street && <div className="flex gap-3" data-id="element-7453" data-mp="sr7cndxye3m8">
                  <dt className="sr-only" data-id="element-7454" data-mp="ss90kj02ikld">Langskomen</dt>
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7455" data-mp="xqakf65binxw" />
                  <dd data-id="element-7456" data-mp="12pn7ns03px10">
                    <a href={`#${VISIT_ID}`} className="text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7457" data-mp="12obzzh3jc54m">
                      Adres, route en kaart
                    </a>
                  </dd>
                </div>}
            </dl>
            {salon.englishNote && <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-7458" data-mp="1gnk32l2tjxe3">{salon.englishNote}.</p>}
          </div>

          {/* Het ene moment van deze pagina: de vragen komen één keer rustig binnen. */}
          {visible.length > 0 && <Reveal className="lg:col-span-7 lg:pt-2" data-id="element-7459" data-mp="1ve14w5jd8pbi">
              <div className="flex items-center gap-4" data-id="element-7460" data-mp="1vfca89sgm54q">
                {/* Decoratief: haar naam staat in de tekst ernaast. */}
                <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7461" data-mp="1zd9x35yrcf4y" />
                <h2 className="type-sub text-ink" data-id="element-7462" data-mp="3fvtrt1i7815a">{fill(copy.channelsTitle)}</h2>
              </div>
              <ul className="mt-8 divide-y divide-ink/15 border-y border-ink/15" data-id="element-7463" data-mp="3aw76e4s7vele">
                {visible.map(({
              key,
              channel,
              action
            }) => <li key={key} className="grid gap-3 py-7 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-8" data-id="element-7464" data-mp="36009f1hstdfl">
                    <div data-id="element-7465" data-mp="7i2zd15gm8qb">
                      <h3 className="type-title text-ink" data-id="element-7466" data-mp="2gnfvdixkaa1">{fill(channel.title)}</h3>
                      {channel.body && <p className="mt-1.5 max-w-md text-[15px] leading-relaxed text-muted" data-id="element-7467" data-mp="3tvb96vt60c4m">{fill(channel.body)}</p>}
                    </div>
                    {channel.cta && <ChannelAction action={action} label={fill(channel.cta)} data-id="element-7468" data-mp="3ounkigses7dk" />}
                  </li>)}
              </ul>
            </Reveal>}
        </div>
      </section>

      {/* Het grote beeld van Contact: een rustige foto van de salon (docs/BEELDEN.md, C1). Bewust zonder beweging. */}
      <div className="bg-sand" data-id="element-7469" data-mp="3uoklj4bgzhyo">
        <ImageSlot code="C1" media={mediaOr(copy.door.media, {
        image: placeholderImages.contactDoor,
        alt: 'Sfeerbeeld: een rustige voordeur in daglicht, met een plant ernaast'
      })} className="aspect-[4/3] w-full sm:aspect-[21/9]" sizes="100vw" data-id="element-7470" data-mp="3uoucau4c9t5f" />
      </div>

      <ContactVisit data-id="element-7471" data-mp="cig3taouabef" />

      <LocalIntro data-id="element-7472" data-mp="ck0u2o44f86h" />
    </>;
}

/** De actie bij een soort vraag: een rustige tekstlink, zodat de hero-knoppen de hoofdactie blijven. */
function ChannelAction({
  action,
  label
}: {
  action: Action;
  label: string;
}) {
  const content = <>
      {label}
      <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7473" data-mp="8h48qpn74xyu" />
    </>;
  if (action.kind === 'button') {
    return <button type="button" onClick={action.onClick} aria-haspopup="dialog" className={`${ACTION_CLS} justify-self-start py-1`} data-id="element-7474" data-mp="2t84rd2hchrin">
        {content}
      </button>;
  }
  return <a href={action.href} {...action.external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} className={`${ACTION_CLS} justify-self-start py-1`} data-id="element-7475" data-mp="2goznc0m17dqp">
      {content}
      {action.srSuffix && <span className="sr-only" data-id="element-7476" data-mp="2pp5znj5zu7oa">{action.srSuffix}</span>}
    </a>;
}